import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleContent from "../components/ArticleContent";
import RelatedArticles from "../components/RelatedArticles";
import RelatedProducts from "../components/RelatedProducts";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === id);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight;
      const scrolled = Math.max(0, -rect.top);
      const progress = Math.min(100, (scrolled / total) * 100);
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!article) {
    return (
      <main className="min-h-screen">
        <SiteHeader />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20">
          <p
            className="text-[#7a6a5a] text-[1.1875rem] mb-6"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            找不到此文章
          </p>
          <button
            onClick={() => navigate("/knowledge")}
            className="px-8 py-3 bg-[#8b6f47] text-white rounded-full text-[1rem] cursor-pointer hover:bg-[#7a5f3a] transition-colors whitespace-nowrap"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            返回知識專欄
          </button>
        </div>
        <SiteFooter />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      {/* Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 h-0.5 bg-[#c9a96e] z-[60] transition-all duration-100"
        style={{ width: `${scrollProgress}%` }}
      />

      <SiteHeader />

      {/* Hero */}
      <section className="relative pt-20 overflow-hidden">
        <div className="w-full h-[55vh] min-h-[380px] relative">
          <img
            src={article.coverImg}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1e1a16]/80 via-[#1e1a16]/30 to-transparent"></div>

          {/* Hero Content */}
          <div className="absolute bottom-0 left-0 w-full px-6 md:px-16 pb-12">
            <div className="max-w-3xl mx-auto">
              <div
                className={`transition-all duration-1000 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 rounded-full text-[0.875rem] font-medium"
                    style={{
                      color: article.tagColor,
                      backgroundColor: `${article.tagBg}cc`,
                      fontFamily: "'Noto Serif TC', serif",
                    }}
                  >
                    {article.tag}
                  </span>
                  <span
                    className="text-white/60 text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {article.readTime}
                  </span>
                  <span
                    className="text-white/40 text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {article.date}
                  </span>
                </div>
                <h1
                  className="text-white font-bold leading-[1.3] mb-3"
                  style={{
                    fontSize: "clamp(2rem, 4vw, 2.75rem)",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.title}
                </h1>
                <p
                  className="text-white/70 leading-[1.75]"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.subtitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <section className="py-16 md:py-20 bg-white" ref={contentRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-16">

            {/* Main Content */}
            <div>
              {/* Summary */}
              <div className="bg-[#f5f0e8] rounded-2xl p-8 mb-12">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-file-text-line text-[#8b6f47]"></i>
                  <span
                    className="text-[#8b6f47] text-[1rem] font-medium"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    文章摘要
                  </span>
                </div>
                <p
                  className="text-[#5a4e45] leading-[1.75]"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.summary}
                </p>
              </div>

              {/* Content */}
              <ArticleContent article={article} />

              {/* Back Button */}
              <div className="mt-14 pt-10 border-t border-[#e8e0d5]">
                <button
                  onClick={() => { navigate("/knowledge"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="inline-flex items-center gap-2 text-[#8b6f47] font-medium text-[1rem] cursor-pointer hover:gap-3 transition-all"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  <i className="ri-arrow-left-line"></i>
                  返回知識專欄
                </button>
              </div>

              {/* Related */}
              <RelatedArticles relatedIds={article.relatedIds} currentId={article.id} />
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-8">
                {/* Table of Contents */}
                <div className="bg-[#faf8f4] rounded-2xl p-6">
                  <h4
                    className="text-[#2d2520] font-bold mb-5 text-[1.1875rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    文章目錄
                  </h4>
                  <ul className="space-y-3">
                    {article.sections
                      .filter((s) => s.type === "heading")
                      .map((s, i) => (
                        <li key={i}>
                          <a
                            href={`#section-${article.sections.findIndex(
                              (sec) => sec.type === "heading" && sec.content === s.content
                            )}`}
                            className="flex items-start gap-2 text-[#7a6a5a] hover:text-[#8b6f47] transition-colors text-[1rem] leading-[1.5] cursor-pointer"
                            style={{ fontFamily: "'Noto Serif TC', serif" }}
                          >
                            <span className="text-[#c9a96e] mt-1 flex-shrink-0">·</span>
                            {s.content}
                          </a>
                        </li>
                      ))}
                  </ul>
                </div>

                {/* Related Products */}
                <RelatedProducts article={article} />

                {/* All Articles */}
                <div className="bg-[#faf8f4] rounded-2xl p-6">
                  <h4
                    className="text-[#2d2520] font-bold mb-5 text-[1.1875rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    所有文章
                  </h4>
                  <ul className="space-y-4">
                    {articles.map((a) => (
                      <li key={a.id}>
                        <button
                          onClick={() => {
                            navigate(`/knowledge/${a.id}`);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className={`text-left w-full text-[1rem] leading-[1.5] cursor-pointer transition-colors ${
                            a.id === article.id
                              ? "text-[#8b6f47] font-semibold"
                              : "text-[#7a6a5a] hover:text-[#8b6f47]"
                          }`}
                          style={{ fontFamily: "'Noto Serif TC', serif" }}
                        >
                          {a.id === article.id && (
                            <i className="ri-arrow-right-s-line mr-1 text-[#8b6f47]"></i>
                          )}
                          {a.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Card */}
                <div className="bg-[#1e1a16] rounded-2xl p-6 text-center">
                  <i className="ri-sparkling-2-line text-[#c9a96e] text-2xl mb-3 block"></i>
                  <p
                    className="text-white font-semibold mb-2 text-[1.1875rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    不確定選哪個？
                  </p>
                  <p
                    className="text-white/50 text-[1rem] mb-5 leading-[1.6]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    做個能量測驗，精準找到最適合你的飾品
                  </p>
                  <button
                    onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="w-full py-3 bg-[#c9a96e] text-[#1e1a16] rounded-full text-[1rem] font-semibold cursor-pointer hover:bg-[#b8945a] transition-colors whitespace-nowrap"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    立即測驗
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
